import { useState, useCallback, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Loader2, MonitorPlay, Minimize, Grid3x3 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/utils/cn';
import { Tooltip } from '@/components/ui/tooltip';
import { VersionSelector, type PlayableVersion } from '@/components/version-selector';
import { CoordPicker, fmtPoint, fmtPath, copyText, type PickPoint, type CoordPickerAnchor } from './coord-picker';

export interface PreviewFrameHandle {
  /** 切换全屏（进入/退出） */
  toggleFullscreen: () => void;
}

interface PreviewFrameProps {
  src?: string;
  loading?: boolean;
  refreshKey?: number;
  /** 流水线状态文本，覆盖默认占位文字 */
  statusText?: string;
  /** 是否显示加载动画（生成中） */
  showSpinner?: boolean;
  /** 版本列表 */
  versions?: PlayableVersion[];
  /** 当前版本ID */
  currentVersionId?: string;
  /** 版本切换回调 */
  onVersionChange?: (versionId: string) => void;
  /** 接受草稿回调 */
  onAcceptDraft?: (versionId: string) => void;
  /** 丢弃草稿回调 */
  onDiscardDraft?: (versionId: string) => void;
  /** FEAT-20260911-PW-019 Layer 4：坐标取点文案插入聊天输入框（走 prefill） */
  onInsertPrompt?: (text: string) => void;
}

function loadOrientation(): boolean {
  try { return localStorage.getItem('playgen.previewLandscape') === '1'; } catch { return false; }
}

/** Layer 4：坐标网格开关持久化（默认关闭） */
function loadGridPref(): boolean {
  try { return localStorage.getItem('playgen.previewGrid') === '1'; } catch { return false; }
}

/**
 * PreviewFrame — 试玩预览容器
 *
 * 功能：
 * 1. 横竖屏切换（localStorage 持久化）
 * 2. iframe 加载指示器（src 变化时显示 spinner，onLoad 后隐藏）
 * 3. Fullscreen API 真正全屏（通过 forwardRef 暴露 toggleFullscreen）
 * 4. 版本选择器（草稿接受/丢弃）
 */
export const PreviewFrame = forwardRef<PreviewFrameHandle, PreviewFrameProps>(function PreviewFrame(
  {
    src, loading = false, refreshKey = 0, statusText, showSpinner,
    versions = [], currentVersionId, onVersionChange, onAcceptDraft, onDiscardDraft, onInsertPrompt,
  },
  ref,
) {
  const [landscape, setLandscape] = useState(loadOrientation);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loadKey, setLoadKey] = useState(0); // BUG-20260910-PW-015: 竖/横屏切时递增以强制重挂 iframe（runtime resize 重算位置）
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const readyTimerRef = useRef<number | null>(null);
  const defaultText = statusText ?? '等待生成...';

  // ── FEAT-20260911-PW-019 Layer 4：坐标网格 + Shift 取点 ──
  const [gridOn, setGridOn] = useState(loadGridPref);
  const [gridSupported, setGridSupported] = useState<boolean | null>(null);
  const [picks, setPicks] = useState<PickPoint[]>([]);
  const [selectedPickId, setSelectedPickId] = useState<string | null>(null);
  const [pickerAnchor, setPickerAnchor] = useState<CoordPickerAnchor | null>(null);
  const gridReadyRef = useRef(false);
  const gridOnRef = useRef(gridOn);
  const iframeLoadingRef = useRef(iframeLoading);
  const gridProbeTimerRef = useRef<number | null>(null);

  console.log('[PreviewFrame] 渲染:', { versionsCount: versions.length, currentVersionId, hasOnVersionChange: !!onVersionChange });

  const setOrientation = useCallback((v: boolean) => {
    setLandscape(v);
    try { localStorage.setItem('playgen.previewLandscape', v ? '1' : '0'); } catch { /* ignore */ }
    // BUG-20260910-PW-015: 横竖屏切换后 container 尺寸变化，runtime 需要在正确的容器尺寸下重算
    setLoadKey(k => k + 1);
    setIframeLoading(true);
  }, []);

  // Fullscreen API：通过 ref 暴露给父组件
  useImperativeHandle(ref, () => ({
    toggleFullscreen: () => {
      const el = containerRef.current;
      if (!el) return;
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        el.requestFullscreen?.();
      }
    },
  }), []);

  // 监听 fullscreen 状态变化（ ESC 退出等）
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // src 或 refreshKey 变化时重置 iframe 加载状态
  useEffect(() => {
    setIframeLoading(Boolean(src));
    if (readyTimerRef.current !== null) {
      window.clearTimeout(readyTimerRef.current);
      readyTimerRef.current = null;
    }
    return () => {
      if (readyTimerRef.current !== null) {
        window.clearTimeout(readyTimerRef.current);
        readyTimerRef.current = null;
      }
    };
  }, [src, refreshKey]);

  // 游戏脚本会在首帧绘制后发送 playgen-ready；跨域 iframe 也能安全比较
  // event.source。不要在 iframe 的 load 事件刚触发时立即撤掉遮罩，WebView
  // 此时经常还没完成 canvas/WebGL 初始化，过早截图就会看到黑屏。
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (event.data?.type === 'playgen-ready') {
        if (readyTimerRef.current !== null) window.clearTimeout(readyTimerRef.current);
        readyTimerRef.current = null;
        setIframeLoading(false);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // ── Layer 4：坐标网格交互（FEAT-20260911-PW-019） ──

  useEffect(() => { gridOnRef.current = gridOn; }, [gridOn]);
  useEffect(() => { iframeLoadingRef.current = iframeLoading; }, [iframeLoading]);
  useEffect(() => () => {
    if (gridProbeTimerRef.current !== null) window.clearTimeout(gridProbeTimerRef.current);
  }, []);

  const postToFrame = useCallback((msg: Record<string, unknown>) => {
    try { iframeRef.current?.contentWindow?.postMessage(msg, '*'); } catch { /* ignore */ }
  }, []);

  /** 产物能力协商与取点回传（event.source 校验沿用 playgen-ready 做法） */
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      const d = event.data;
      if (!d || typeof d !== 'object') return;
      switch (d.type) {
        case 'vp-ready': {
          gridReadyRef.current = true;
          setGridSupported(true);
          if (gridProbeTimerRef.current !== null) {
            window.clearTimeout(gridProbeTimerRef.current);
            gridProbeTimerRef.current = null;
          }
          // 重挂/重载（横竖屏切换、刷新）后按父页当前状态自动重发（方案 7.8）
          if (gridOnRef.current) {
            postToFrame({ type: 'vp-grid', enabled: true, step: 10, labels: true, safeArea: true });
            postToFrame({ type: 'vp-pick-mode', enabled: true, snap: false });
          }
          break;
        }
        case 'vp-pick': {
          const p: PickPoint = {
            id: String(d.id ?? ''), cell: String(d.cell ?? ''),
            xPct: Number(d.xPct) || 0, yPct: Number(d.yPct) || 0,
            nearest: d.nearest ?? null,
          };
          if (!p.id) break;
          setPicks((prev) => [...prev.filter((q) => q.id !== p.id), p].slice(-5));
          setSelectedPickId(p.id);
          break;
        }
        case 'vp-pick-cleared':
          setPicks([]);
          setSelectedPickId(null);
          break;
        case 'vp-grid-changed': {
          // G 键在 iframe 内切换网格：同步父页按钮态
          const on = !!d.enabled;
          setGridOn(on);
          try { localStorage.setItem('playgen.previewGrid', on ? '1' : '0'); } catch { /* ignore */ }
          if (!on) { setPicks([]); setSelectedPickId(null); }
          break;
        }
        default:
          break;
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [postToFrame]);

  /** src / refreshKey / loadKey 变化：能力与取点重置；网格处于开启态时做一次恢复探测 */
  useEffect(() => {
    gridReadyRef.current = false;
    setGridSupported(null);
    setPicks([]);
    setSelectedPickId(null);
    if (gridProbeTimerRef.current !== null) {
      window.clearTimeout(gridProbeTimerRef.current);
      gridProbeTimerRef.current = null;
    }
    // 恢复探测失败只做「当次置灰」，不清 localStorage 偏好（仅用户手动开关才写偏好）——
    // 否则从旧产物/非视频玩法切回支持产物时无法按用户意愿自动恢复网格
    const prefOn = gridOnRef.current || loadGridPref();
    if (prefOn && !gridOnRef.current) {
      setGridOn(true);
      gridOnRef.current = true;
    }
    const armProbe = (delay: number, allowGrace: boolean) => {
      gridProbeTimerRef.current = window.setTimeout(() => {
        gridProbeTimerRef.current = null;
        if (gridReadyRef.current) return;
        if (allowGrace && iframeLoadingRef.current) {
          // iframe 仍在加载（慢网/大产物）：再宽限一轮，vp-ready 需等文档解析 + 延迟发送
          armProbe(1500, false);
          return;
        }
        // 旧产物/非视频玩法：静默置灰（tooltip 说明），避免打扰
        setGridSupported(false);
        setGridOn(false);
        gridOnRef.current = false;
      }, delay);
    };
    if (prefOn && src) armProbe(2500, true);
  }, [src, refreshKey, loadKey]);

  const toggleGrid = useCallback(() => {
    if (!src) return;
    const next = !gridOn;
    setGridOn(next);
    try { localStorage.setItem('playgen.previewGrid', next ? '1' : '0'); } catch { /* ignore */ }
    if (next) {
      postToFrame({ type: 'vp-grid', enabled: true, step: 10, labels: true, safeArea: true });
      postToFrame({ type: 'vp-pick-mode', enabled: true, snap: false });
      try {
        if (localStorage.getItem('playgen.previewGrid.tipShown') !== '1') {
          toast('已开启坐标网格：格号 A~J / 1~10，格中心 = 十进制百分比（E6 = x45% y55%）。按住 Shift 点击画面可取坐标并一键复制。', { duration: 4000 });
          localStorage.setItem('playgen.previewGrid.tipShown', '1');
        }
      } catch { /* ignore */ }
      // 能力探测：400ms 无 vp-ready 回执 → 判定产物不支持（方案 7.8）
      if (!gridReadyRef.current && gridSupported !== false) {
        if (gridProbeTimerRef.current !== null) window.clearTimeout(gridProbeTimerRef.current);
        gridProbeTimerRef.current = window.setTimeout(() => {
          gridProbeTimerRef.current = null;
          if (gridReadyRef.current) return;
          if (iframeLoadingRef.current) {
            // iframe 仍在加载：再宽限一轮（vp-ready 需等文档解析 + 延迟发送）
            gridProbeTimerRef.current = window.setTimeout(() => {
              gridProbeTimerRef.current = null;
              if (gridReadyRef.current) return;
              setGridSupported(false);
              setGridOn(false);
              try { localStorage.setItem('playgen.previewGrid', '0'); } catch { /* ignore */ }
              toast.error('当前试玩版本不支持坐标网格，重新生成后可用');
            }, 1500);
            return;
          }
          setGridSupported(false);
          setGridOn(false);
          try { localStorage.setItem('playgen.previewGrid', '0'); } catch { /* ignore */ }
          toast.error('当前试玩版本不支持坐标网格，重新生成后可用');
        }, 400);
      }
    } else {
      postToFrame({ type: 'vp-grid', enabled: false });
      postToFrame({ type: 'vp-pick-mode', enabled: false });
      setPicks([]);
      setSelectedPickId(null);
    }
  }, [src, gridOn, gridSupported, postToFrame]);

  /** 气泡卡锚点：iframe 实时 rect + 点百分比 → 容器内绝对坐标（全屏/resize 时重算） */
  useEffect(() => {
    const compute = () => {
      const container = containerRef.current;
      const iframe = iframeRef.current;
      const sel = picks.find((p) => p.id === selectedPickId) ?? null;
      if (!container || !iframe || !sel) { setPickerAnchor(null); return; }
      const cr = container.getBoundingClientRect();
      const fr = iframe.getBoundingClientRect();
      const x = fr.left - cr.left + (sel.xPct / 100) * fr.width;
      const y = fr.top - cr.top + (sel.yPct / 100) * fr.height;
      const flipX = x > cr.width * 0.6;
      const flipY = y > cr.height * 0.35;
      setPickerAnchor({ left: flipX ? x - 10 : x + 10, top: flipY ? y - 10 : y + 10, flipX, flipY });
    };
    compute();
    window.addEventListener('resize', compute);
    document.addEventListener('fullscreenchange', compute);
    return () => {
      window.removeEventListener('resize', compute);
      document.removeEventListener('fullscreenchange', compute);
    };
  }, [picks, selectedPickId, landscape, isFullscreen]);

  const handleSelectPick = useCallback((id: string) => {
    setSelectedPickId(id);
    postToFrame({ type: 'vp-pick-highlight', id });
  }, [postToFrame]);

  const handleDeletePick = useCallback((id: string) => {
    postToFrame({ type: 'vp-pick-clear', id });
    const next = picks.filter((p) => p.id !== id);
    setPicks(next);
    if (selectedPickId === id) setSelectedPickId(next.length ? next[next.length - 1].id : null);
  }, [picks, selectedPickId, postToFrame]);

  const handleClearPicks = useCallback(() => {
    postToFrame({ type: 'vp-pick-clear' });
    setPicks([]);
    setSelectedPickId(null);
  }, [postToFrame]);

  const handleInsertPick = useCallback((p: PickPoint) => {
    onInsertPrompt?.(`在 ${fmtPoint(p)} 处`);
  }, [onInsertPrompt]);

  const handleCopyPath = useCallback(async () => {
    if (picks.length < 2) return;
    const ok = await copyText(fmtPath(picks));
    if (ok) toast.success('已复制路径描述'); else toast.error('复制失败');
  }, [picks]);

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex items-center gap-2">
        {/* 横竖屏切换 */}
        <div className="flex items-center gap-0.5 rounded bg-muted/50 p-0.5">
          <button onClick={() => setOrientation(false)} className={cn('rounded px-2 py-0.5 text-sm', !landscape ? 'bg-primary/15 text-primary' : 'text-foreground/80 hover:text-foreground')}>📱 竖屏</button>
          <button onClick={() => setOrientation(true)} className={cn('rounded px-2 py-0.5 text-sm', landscape ? 'bg-primary/15 text-primary' : 'text-foreground/80 hover:text-foreground')}>🖥 横屏</button>
          {isFullscreen && (
            <button
              onClick={() => document.exitFullscreen()}
              className="ml-1 flex items-center gap-1 rounded px-2 py-0.5 text-sm text-foreground/80 hover:text-foreground"
              title="退出全屏"
            >
              <Minimize size={12} />
            </button>
          )}
        </div>

        {/* Layer 4：坐标网格开关（FEAT-20260911-PW-019） */}
        <Tooltip content={!src ? '生成完成后可用' : gridSupported === false ? '当前试玩版本不支持坐标网格，重新生成后可用' : '开启后：按住 Shift 点击画面取坐标（触屏长按）；Esc 清除取点'}>
          <button
            onClick={toggleGrid}
            disabled={!src || gridSupported === false}
            className={cn(
              'flex items-center gap-1 rounded px-2 py-0.5 text-sm',
              gridOn ? 'bg-primary/15 text-primary' : 'text-foreground/80 hover:text-foreground',
              (!src || gridSupported === false) && 'cursor-not-allowed opacity-50',
            )}
          >
            <Grid3x3 size={13} />
            坐标网格
          </button>
        </Tooltip>

        {/* 版本选择器 */}
        {versions.length > 0 && onVersionChange && (
          <VersionSelector
            versions={versions}
            currentVersionId={currentVersionId}
            onVersionChange={onVersionChange}
            onAcceptDraft={onAcceptDraft}
            onDiscardDraft={onDiscardDraft}
          />
        )}
      </div>

      <div
        ref={containerRef}
        className={cn(
          'relative overflow-hidden border-[3px] border-border/50 bg-background p-1 shadow-xl shadow-black/20 transition-all',
          // 全屏时去掉圆角和固定宽度，撑满屏幕
          isFullscreen ? 'rounded-none border-0 flex items-center justify-center' : 'rounded-[2rem]',
          !isFullscreen && (landscape ? 'aspect-video' : 'aspect-[9/16]'),
        )}
        style={isFullscreen ? { width: '100%', height: '100%' } : { width: landscape ? 500 : 340 }}
      >
        {loading ? (
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 text-foreground/80">
            <Loader2 size={28} className="animate-spin text-neon-cyan" />
            <span className="text-sm">加载预览...</span>
          </div>
        ) : src ? (
          <>
            <iframe
              ref={iframeRef}
              key={`${refreshKey}-${loadKey}`}
              src={src}
              className="h-full w-full border-0"
              style={isFullscreen ? undefined : { borderRadius: '1.5rem' }}
              title="preview"
              onLoad={() => {
                // 兜底：旧版本产物还未发送 ready 时，最多保留 1.5s 遮罩。
                if (readyTimerRef.current !== null) window.clearTimeout(readyTimerRef.current);
                readyTimerRef.current = window.setTimeout(() => {
                  readyTimerRef.current = null;
                  setIframeLoading(false);
                }, 1500);
              }}
            />
            {/* iframe 内容加载中覆盖层 */}
            {iframeLoading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background/90" style={isFullscreen ? undefined : { borderRadius: '1.5rem' }}>
                <Loader2 size={28} className="animate-spin text-neon-cyan" />
                <span className="text-xs text-muted-foreground">游戏加载中...</span>
              </div>
            )}
          </>
        ) : showSpinner ? (
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 text-muted-foreground">
            <Loader2 size={28} className="animate-spin text-neon-cyan" />
            <span className="text-xs">{defaultText}</span>
          </div>
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-foreground/80">
            <MonitorPlay size={32} className="opacity-50" />
            <span className="text-sm">{defaultText}</span>
          </div>
        )}
        {/* Layer 4：坐标气泡卡（父页面渲染，不污染产物 DOM） */}
        {picks.length > 0 && (
          <CoordPicker
            picks={picks}
            selectedId={selectedPickId}
            anchor={pickerAnchor}
            onSelect={handleSelectPick}
            onDelete={handleDeletePick}
            onClearAll={handleClearPicks}
            onInsert={handleInsertPick}
            onCopyPath={handleCopyPath}
          />
        )}
        {!isFullscreen && <div className="absolute left-1/2 top-2 h-1 w-20 -translate-x-1/2 rounded-full bg-border/60" />}
      </div>
    </div>
  );
});
